import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Theme,
  Typography,
} from '@mui/material';
import { useMemo } from 'react';

import { DialogTitleWithClose } from '@/components/common/DialogTitleWithClose';
import { Spinner } from '@/components/common/Spinner';
import { ApplicationUpdateFormData } from '@/hooks/useApplicationUpdateModal';
import { ApplicationDataGridDetailsProps } from '@/models/Application';
import { ProgramNames } from '@/models/Program';
import { SelectOptionProps } from '@/models/SelectOption';
import { UserType } from '@/models/User';
import { ApplicationStatus } from '@/utils/constants';

import AdditionalStickersField from './fields/AdditionalStickersField';
import ContactSelectField from './fields/ContactSelectField';
import PartnerSelectField from './fields/PartnerSelectField';
import StatusSelectField from './fields/StatusSelectField';

type LoadingState = {
  app: boolean;
  partner: boolean;
  partnerGroup: boolean;
  partners: boolean;
  contacts: boolean;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;

  theme: Theme;
  userType: UserType | null | undefined;
  applicationDataRow: ApplicationDataGridDetailsProps;

  formData: ApplicationUpdateFormData | null;

  onPartnerChange: (id: number | null) => void;
  onContactChange: (id: number | null) => void;
  onStatusChange: (status: ApplicationStatus | null) => void;
  onAdditionalStickersChange: (value: number | null) => void;

  partnerOptions: SelectOptionProps[] | null;
  contactOptions: SelectOptionProps[] | null;
  statusOptions: SelectOptionProps[];

  loading: LoadingState;
  posting: boolean;
};

export function ApplicationUpdateDialog(props: Props) {
  const {
    open,
    onClose,
    onSubmit,

    theme,
    userType,
    applicationDataRow,

    formData,
    onPartnerChange,
    onContactChange,
    onStatusChange,
    onAdditionalStickersChange,

    partnerOptions,
    contactOptions,
    statusOptions,

    loading,
    posting,
  } = props;

  const partnerLoading = useMemo(
    () => loading.app || loading.partnerGroup || loading.partners,
    [loading.app, loading.partnerGroup, loading.partners]
  );
  const contactsLoading = useMemo(() => loading.contacts, [loading.contacts]);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitleWithClose onClose={onClose}>Edit Application Details</DialogTitleWithClose>

      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant='overline2'>Program</Typography>
          <Typography color='textSecondary'>{applicationDataRow?.applicationType}</Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant='overline2'>Application</Typography>
          <Typography color='textSecondary'>{applicationDataRow?.applicationId}</Typography>
        </Box>

        {formData == null ? (
          <Box sx={{ py: 3 }}>
            <Spinner type='circular' />
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              <PartnerSelectField
                loading={partnerLoading}
                userType={userType}
                value={formData.partnerId}
                options={partnerOptions}
                onChange={onPartnerChange}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <ContactSelectField
                loading={contactsLoading}
                userType={userType}
                value={formData.contactId}
                options={contactOptions}
                onChange={onContactChange}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <StatusSelectField
                loading={formData.status == null}
                value={formData.status}
                options={statusOptions}
                onChange={onStatusChange}
              />
            </Box>

            <Box sx={{ mb: 1 }}>
              {applicationDataRow.programId === ProgramNames.TreeCityUSA && (
                <AdditionalStickersField
                  value={formData.additionalStickers}
                  onChange={onAdditionalStickersChange}
                />
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant='secondary' size='large' onClick={onClose}>
          Cancel
        </Button>

        <Button variant='primary' size='large' onClick={onSubmit} disabled={posting}>
          {posting ? (
            <>
              <span>Updating ... </span>
              <CircularProgress
                style={{ color: theme.palette.grey[500], marginLeft: '5px' }}
                size='16px'
              />
            </>
          ) : (
            'Update Application'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
